import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import styled from 'styled-components'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import SearchIcon from '@mui/icons-material/Search'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import { getCurrentUser } from 'features/auth/ducks/selectors'
import { useWorkflows } from 'features/tours/WorkflowProvider'
import { WORKFLOWS } from 'features/tours/workflows'
import { groupByCategory, searchWorkflows } from 'features/tours/catalogue'
import { availabilityOf } from 'features/tours/requirements'
import { isWorkflowDone } from 'features/tours/storage'
import useMyBuildings from 'features/tours/useMyBuildings'
import { suggestGuides } from './suggestGuides'

const FAQ_URL = 'https://www.buildingpassport.com/faqs'

const sameAvailability = (a, b) =>
  a.length === b.length && a.every((item, i) => item.available === b[i].available && item.reason === b[i].reason)

const HelpPanel = ({ open, onClose, workflows = WORKFLOWS }) => {
  const [query, setQuery] = useState('')
  const currentUser = useSelector(getCurrentUser)
  const { startWorkflow } = useWorkflows()
  const { buildings, loading } = useMyBuildings(open)
  const hasBuildings = loading || buildings.length > 0
  const location = useLocation()
  const availabilities = useSelector(
    s => workflows.map(workflow => availabilityOf(workflow, s, { hasBuildings })),
    sameAvailability,
  )
  const availabilityByKey = useMemo(
    () => new Map(workflows.map((workflow, i) => [workflow.key, availabilities[i]])),
    [workflows, availabilities],
  )
  const suggested = useMemo(
    () => suggestGuides(workflows, location.pathname, {
      completed: workflow => isWorkflowDone(currentUser?.id, workflow.key, workflow.version || 1),
      isAvailable: workflow => (availabilityByKey.get(workflow.key) || { available: true }).available,
    }),
    [workflows, location.pathname, currentUser?.id, availabilityByKey],
  )
  const searchRef = useRef(null)

  useEffect(() => {
    if (!open) return undefined
    setQuery('')
    const timer = setTimeout(() => searchRef.current?.focus(), 0)
    const onKey = e => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => {
      clearTimeout(timer)
      window.removeEventListener('keydown', onKey)
    }
  }, [open, onClose])

  const results = useMemo(() => searchWorkflows(workflows, query), [workflows, query])
  const groups = useMemo(() => groupByCategory(results), [results])

  if (!open) return null

  const show = workflow => {
    onClose()
    startWorkflow(workflow.key)
  }

  return createPortal(
    <>
      <Scrim onClick={onClose} aria-hidden='true' />
      <Drawer role='dialog' aria-modal='true' aria-label='Step-by-step guides'>
        <Head>
          <Heading>Step-by-step guides</Heading>
          <Close type='button' onClick={onClose} aria-label='Close guides'>
            <CloseRoundedIcon />
          </Close>
        </Head>
        <Lead>Pick a guide and we will show you, step by step, on your own screens. Nothing is saved until you press Save yourself.</Lead>
        <SearchWrap>
          <SearchIcon aria-hidden='true' style={{ fontSize: 20, color: '#667085' }} />
          <SearchInput
            ref={searchRef}
            type='search'
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder='What do you want to do?'
            aria-label='What do you want to do?'
          />
        </SearchWrap>
        <Body>
          {suggested.length > 0 && !query.trim() && (
            <Suggested>
              <GroupHeading>Suggested here</GroupHeading>
              <SuggestedLead>Guides for the page you are on.</SuggestedLead>
              <List>
                {suggested.map(workflow => (
                  <GuideRow
                    key={workflow.key}
                    workflow={workflow}
                    availability={{ available: true, reason: null }}
                    done={false}
                    onShow={() => show(workflow)}
                  />
                ))}
              </List>
            </Suggested>
          )}
          {groups.length === 0 ? (
            <Empty>No guides match that. Try other words, or ask Guru.</Empty>
          ) : (
            groups.map(group => (
              <Group key={group.key}>
                <GroupHeading>{group.label}</GroupHeading>
                <List>
                  {group.workflows.map(workflow => {
                    const availability = availabilityByKey.get(workflow.key) || { available: true, reason: null }
                    const done = isWorkflowDone(currentUser?.id, workflow.key, workflow.version || 1)
                    return (
                      <GuideRow
                        key={workflow.key}
                        workflow={workflow}
                        availability={availability}
                        done={done}
                        onShow={() => show(workflow)}
                      />
                    )
                  })}
                </List>
              </Group>
            ))
          )}
        </Body>
        <Foot>
          <FootLink href={FAQ_URL} target='_blank' rel='noopener noreferrer'>Frequently asked questions</FootLink>
          <Shortcut>Press <Kbd>?</Kbd> any time to open these guides</Shortcut>
        </Foot>
      </Drawer>
    </>,
    document.body
  )
}

export default HelpPanel

const GuideRow = ({ workflow, availability, done, onShow }) => (
  <Row aria-label={workflow.title} $disabled={!availability.available}>
    <RowMain>
      <RowTitle>
        {workflow.title}
        {done && (
          <DoneTag>
            <CheckCircleRoundedIcon aria-hidden='true' style={{ fontSize: 16 }} />
            Done
          </DoneTag>
        )}
      </RowTitle>
      <RowSummary>{workflow.summary}</RowSummary>
      <RowMeta>{`About ${workflow.minutes || 2} minute${(workflow.minutes || 2) === 1 ? '' : 's'}`}</RowMeta>
      {!availability.available && <Reason>{availability.reason}</Reason>}
    </RowMain>
    <ShowMe
      type='button'
      disabled={!availability.available}
      onClick={onShow}
      aria-label={`Show me how to ${workflow.title.charAt(0).toLowerCase()}${workflow.title.slice(1)}`}
    >
      Show me
    </ShowMe>
  </Row>
)

const Scrim = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100399;
  background: rgba(12, 18, 41, 0.35);
`

const Drawer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 100400;
  width: min(480px, 100vw);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-left: 1px solid #e5e7eb;
  box-shadow: -4px 0 14px rgba(12, 18, 41, 0.12);

  @media (max-width: 768px) {
    width: 100vw;
    border-left: none;
  }
`

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px 6px;
`

const Heading = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #0C1229;
`

const Close = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: #344054;
  cursor: pointer;
  &:hover { background: #f2f4f7; }
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 2px; }
`

const Lead = styled.p`
  margin: 0;
  padding: 0 20px 12px;
  font-size: 15px;
  line-height: 1.5;
  color: #475467;
`

const SearchWrap = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 20px 12px;
  padding: 0 12px;
  min-height: 44px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  &:focus-within { border-color: #33becc; }
`

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font: inherit;
  font-size: 16px;
  color: #0C1229;
`

const Suggested = styled.div`
  padding: 0 0 4px;
  border-bottom: 1px solid #f2f4f7;
`

const SuggestedLead = styled.p`
  margin: 0 0 8px;
  font-size: 15px;
  line-height: 1.45;
  color: #475467;
`

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 20px 12px;
`

const Empty = styled.p`
  margin: 24px 0;
  font-size: 15px;
  color: #475467;
`

const Group = styled.section`
  margin-top: 8px;
`

const GroupHeading = styled.h3`
  margin: 12px 0 4px;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #667085;
`

const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`

const Row = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #f2f4f7;
  opacity: ${({ $disabled }) => ($disabled ? 0.75 : 1)};
`

const RowMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
  min-width: 0;
`

const RowTitle = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 16px;
  font-weight: 600;
  color: #0C1229;
`

const DoneTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #dcfce7;
  color: #15803d;
  font-size: 15px;
  font-weight: 600;
`

const RowSummary = styled.span`
  font-size: 15px;
  line-height: 1.45;
  color: #475467;
`

const RowMeta = styled.span`
  font-size: 15px;
  color: #667085;
`

const Reason = styled.span`
  font-size: 15px;
  color: #c2410c;
`

const ShowMe = styled.button`
  flex-shrink: 0;
  min-height: 40px;
  padding: 0 16px;
  border: 1px solid #33becc;
  border-radius: 22px;
  background: #fff;
  color: #0C1229;
  font: inherit;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  &:hover:not(:disabled) { background: #e6f7f9; }
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 2px; }
  &:disabled { border-color: #e5e7eb; color: #98a2b3; cursor: not-allowed; }
`

const Foot = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 14px 20px calc(14px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid #f2f4f7;
`

const FootLink = styled.a`
  display: inline-flex;
  align-items: center;
  min-height: 40px;
  font-size: 15px;
  font-weight: 600;
  color: #2aa6b3;
`

const Shortcut = styled.span`
  font-size: 15px;
  color: #667085;
`

const Kbd = styled.kbd`
  padding: 1px 6px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-family: inherit;
  font-size: 15px;
`
